'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useNotification } from '@/contexts/NotificationContext';

export type TvPolicyProposal = {
  id: number;
  proposedMinQty: number | null;
  proposedReorderPoint: number | null;
  proposedTargetQty: number | null;
  weeklyDepletionRate: string;
  sampleWeeks: number;
  receiveLocation?: { id: number; name?: string | null };
  inventoryItem?: { id: number; upc: string; name?: string | null };
};

type Props = {
  fetchProposals: () => Promise<TvPolicyProposal[]>;
  onAccept: (id: number) => Promise<void>;
  onDismiss: (id: number) => Promise<void>;
};

const TvPolicyProposalsPanel: React.FC<Props> = ({ fetchProposals, onAccept, onDismiss }) => {
  const { showSuccess, showError } = useNotification();
  const [rows, setRows] = useState<TvPolicyProposal[]>([]);

  const load = useCallback(async () => {
    try {
      setRows(await fetchProposals());
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load proposals');
    }
  }, [fetchProposals, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  if (rows.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Suggested policy updates (from visit depletion)
      </Typography>
      {rows.map((p) => (
        <Alert
          key={p.id}
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() =>
                  void onDismiss(p.id).then(() => {
                    showSuccess('Dismissed');
                    void load();
                  })
                }
              >
                Dismiss
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() =>
                  void onAccept(p.id).then(() => {
                    showSuccess('Applied to replenishment policy');
                    void load();
                  })
                }
              >
                Accept
              </Button>
            </Box>
          }
        >
          {p.receiveLocation?.name ?? 'Location'} — {p.inventoryItem?.upc ?? 'SKU'}: reorder{' '}
          {p.proposedReorderPoint ?? '—'}, target {p.proposedTargetQty ?? '—'} (samples: {p.sampleWeeks})
        </Alert>
      ))}
    </Paper>
  );
};

export default TvPolicyProposalsPanel;
